import React from "react";
import { Box, Flex, MenuItemOption, MenuItemOptionProps, Text } from "@chakra-ui/react";
import { useTranslate } from "@tolgee/react";
import weatherNight from "@mdi/svg/svg/weather-night.svg";
import { trackEvent } from "../backend/utils/helper-methods";
import { colors } from "../constants/GlobalStyles";
import {
  canThisDeviceShowTheDarkTheme,
  JungleColorTheme,
  JungleColorThemePreference,
  resolveColorTheme,
} from "../theme/jungleColorTheme";
import { useJungleColorThemeStore } from "../theme/useJungleColorThemeStore";
import { userStore } from "../zustand/globalStore";

// The switch's size and motion match Chakra's medium Switch, the one on the Settings page
const SWITCH_TRACK_WIDTH = 34;
const SWITCH_TRACK_HEIGHT = 20;
const SWITCH_TRACK_PADDING = 2;
const SWITCH_THUMB_SIZE = SWITCH_TRACK_HEIGHT - 2 * SWITCH_TRACK_PADDING;
const SWITCH_THUMB_TRAVEL = SWITCH_TRACK_WIDTH - SWITCH_THUMB_SIZE - 2 * SWITCH_TRACK_PADDING;

/**
 * Where flipping the switch leaves the preference. The switch shows the theme the page is in, and
 * flipping it asks for the other one. When the theme asked for is the one the automatic appearance shows
 * right now (light by day, dark at night), the page goes back to automatic, so that switching off and on
 * again is an undo and nobody is stranded in a fixed theme they never picked. Anything else is a choice:
 * dark in the daytime stays dark, light at night stays light, until the switch or Settings changes it.
 */
export function getColorThemePreferenceAfterSwitch({
  shouldBeDark,
  automaticColorTheme,
}: {
  shouldBeDark: boolean;
  automaticColorTheme: JungleColorTheme;
}): JungleColorThemePreference {
  const requestedColorTheme = shouldBeDark ? "dark" : "light";
  return requestedColorTheme === automaticColorTheme ? "auto" : requestedColorTheme;
}

function DarkModeSwitchPicture({ isOn }: { isOn: boolean }) {
  // Only a picture: the whole row is the control (a menu checkbox), so the switch itself takes no focus
  return (
    <Box
      aria-hidden="true"
      flexShrink={0}
      width={`${SWITCH_TRACK_WIDTH}px`}
      height={`${SWITCH_TRACK_HEIGHT}px`}
      padding={`${SWITCH_TRACK_PADDING}px`}
      borderRadius="full"
      background={isOn ? colors.primaryAction.default : colors.secondaryAction.inactive}
      transition="background-color 150ms ease"
    >
      <Box
        width={`${SWITCH_THUMB_SIZE}px`}
        height={`${SWITCH_THUMB_SIZE}px`}
        borderRadius="full"
        background={colors.text.white}
        transform={isOn ? `translateX(${SWITCH_THUMB_TRAVEL}px)` : "translateX(0)"}
        transition="transform 150ms ease"
      />
    </Box>
  );
}

type DarkModeMenuItemProps = Omit<MenuItemOptionProps, "type" | "isChecked" | "onClick" | "icon" | "children">;

/** The dark mode switch in the profile menu (the menu under the learner's name in the navigation). */
export function DarkModeMenuItem(menuItemStyleProps: DarkModeMenuItemProps) {
  const { t } = useTranslate();
  const colorThemePreference = useJungleColorThemeStore((state) => state.colorThemePreference);
  const dayPhase = useJungleColorThemeStore((state) => state.dayPhase);
  const setColorThemePreference = useJungleColorThemeStore((state) => state.setColorThemePreference);

  // An app version that cannot draw its status bar over a dark page keeps every page light
  if (!canThisDeviceShowTheDarkTheme()) return null;

  // The profile menu only exists on the learner's pages, which follow the preference
  const resolveForLearnerPage = (preference: JungleColorThemePreference) =>
    resolveColorTheme({
      colorThemeScope: "app",
      colorThemePreference: preference,
      dayPhase,
      adminToolsColorMode: "light",
    });
  const isDarkModeOn = resolveForLearnerPage(colorThemePreference) === "dark";

  const flipDarkMode = (event: React.MouseEvent) => {
    // The phone's bottom bar opens and closes this menu with a click on its own box, which holds the menu:
    // a click that reached it would close the menu (the share row stops it the same way)
    event.stopPropagation();
    const newColorThemePreference = getColorThemePreferenceAfterSwitch({
      shouldBeDark: !isDarkModeOn,
      automaticColorTheme: resolveForLearnerPage("auto"),
    });
    setColorThemePreference(newColorThemePreference);
    trackEvent(
      "Appearance Setting Changed",
      { color_theme_preference: newColorThemePreference, appearance_control: "menu_switch" },
      userStore.getState().currentUser
    );
  };

  return (
    <MenuItemOption
      {...menuItemStyleProps}
      type="checkbox"
      isChecked={isDarkModeOn}
      // No check mark: the switch at the end of the row shows the state
      icon={null}
      // The menu stays open so the switch is seen to move while the page fades into the other theme
      closeOnSelect={false}
      onClick={flipDarkMode}
    >
      <Flex alignItems="center" width="100%">
        <Box
          as="span"
          aria-hidden="true"
          display="inline-block"
          flexShrink={0}
          boxSize="20px"
          marginEnd="0.75rem"
          bg="currentColor"
          sx={{
            mask: `url("${weatherNight}") center / contain no-repeat`,
            WebkitMask: `url("${weatherNight}") center / contain no-repeat`,
          }}
        />
        <Text as="span" flex="1" textAlign="start">
          {t("settings.appearance.dark_mode", "dark mode")}
        </Text>
        <DarkModeSwitchPicture isOn={isDarkModeOn} />
      </Flex>
    </MenuItemOption>
  );
}
